import { AddOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Table,
  TableContainer,
  TablePagination,
} from '@mui/material'
import { GetPlacesV2Request, PlaceNode } from '@ulysses-inc/harami_api_client'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import placesOperations from 'src/state/ducks/places/operations'
import { GetPlacesFilter } from 'src/state/ducks/places/types'
import { RootState } from 'src/state/store'
import EditPlace from './EditPlace'
import PlaceList from './PlaceList'
import PlacesFilterDropDown from './PlacesFilterDropDown'

interface StateProps {
  places: PlaceNode[]
  isLoading: boolean
  isShowEditPlaceDrawer: boolean
  segmentIndex: number
  activePlaceId?: string
  count: number
  request: GetPlacesV2Request
  updatedPlaces: boolean
  filter: GetPlacesFilter
}

interface DispatchProps {
  getPlacesV2: (request: GetPlacesV2Request, filter?: GetPlacesFilter) => void
  changePlacesV2Page: (
    page: number,
    pageSize?: number,
    filter?: GetPlacesFilter,
  ) => void
  changePlacesV2Size: (pageSize: number, filter?: GetPlacesFilter) => void
  updateSegmentIndex: (index: number) => void
  changeIsShowEditPlaceDrawer: (isShow: boolean) => void
  openAddPlace: () => void
  openEditPlace: (place: PlaceNode) => void
  deletePlace: (id: string) => void
  changeUpdatedPlacesFlag: (edited: boolean) => void
}

type PlacesProps = StateProps & DispatchProps

const PlacesScene = () => {
  const [placeGetTrigger, setPlaceGetTrigger] = useState<boolean>(false)

  // react-reduxのconnect関数の利用をやめた際のなごり
  // (今後このコンポーネントを触ることがあればそのときに解体する)
  const exProps = useProps()

  useEffect(() => {
    // 現場一覧に遷移したとき、props.request が {} になっているため初期値を入れる
    // {} になってしまう原因は別画面と共用のstateを利用しているため、requestが引きづられている
    const getPlaceRequest: GetPlacesV2Request = {
      limit: exProps.request.limit ?? 25,
      offset: exProps.request.offset ?? 0,
    }
    exProps.getPlacesV2(getPlaceRequest, exProps.filter)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setPlaceGetTrigger(!placeGetTrigger)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 現場の登録または更新があった際に再更新
  useEffect(() => {
    if (!exProps.isLoading && exProps.updatedPlaces) {
      exProps.getPlacesV2(exProps.request, exProps.filter)
      exProps.changeUpdatedPlacesFlag(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exProps.isLoading])

  const [rowsPerPage, setRowsPerPage] = useState(25)

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    // ⚠️ changePlacesV2Page の第一引数は 1 オリジン
    // (元々使用していた) AntDesign の Pagination は 1 オリジン
    // MUI の TablePagination は 0 オリジン
    exProps.changePlacesV2Page(newPage + 1, rowsPerPage, exProps.filter)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const size = parseInt(event.target.value, 10)
    exProps.changePlacesV2Size(size, exProps.filter)
    setRowsPerPage(size)
  }

  // 閉じるボタン or Drawer外のクリックしたとき
  const onClose = () => {
    exProps.updateSegmentIndex(0)
    exProps.changeIsShowEditPlaceDrawer(false)
  }

  const currentPage =
    exProps.request.offset === undefined ||
    exProps.request.limit === undefined ||
    exProps.request.limit === 0
      ? 0
      : exProps.request.offset / exProps.request.limit

  return (
    <Stack
      sx={{
        height: 'calc(100vh - var(--banner-height, 0px))',
      }}
    >
      <Box
        sx={{
          height: 72,
          width: '100%',
          alignItems: 'center',
          boxSizing: 'border-box',
          display: 'block',
          flexDirection: 'row',
        }}
      >
        <Header>
          <HeaderTitle title="現場" />
        </Header>
      </Box>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        sx={{ py: 2, px: 3 }}
      >
        <PlacesFilterDropDown
          request={exProps.request}
          updatedPlaces={exProps.updatedPlaces}
          isLoadingPlace={exProps.isLoading}
          placeGetTrigger={placeGetTrigger}
          setPlaceGetTrigger={setPlaceGetTrigger}
        />
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={() => exProps.openAddPlace()}
        >
          現場を追加
        </Button>
      </Box>
      <TableContainer
        sx={{
          overflow: 'auto',
          position: 'relative',
          flexGrow: 1,
          bgcolor: 'white',
        }}
      >
        <Table sx={{ bgcolor: 'white', width: '100%' }} stickyHeader>
          <PlaceList {...exProps} />
        </Table>
      </TableContainer>
      <Box>
        <Divider></Divider>
        <TablePagination
          component="div"
          count={exProps.count}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={exProps.request.limit ?? 25}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="1ページあたり:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}〜${to} (${count !== -1 ? count : `不明`} 件中)`
          }
          sx={{
            '& .MuiTablePagination-selectLabel': {
              margin: 0,
            },
            '& .MuiTablePagination-displayedRows': {
              margin: 0,
            },
            bgcolor: 'white',
          }}
        ></TablePagination>
      </Box>
      <Drawer
        anchor="right"
        open={exProps.isShowEditPlaceDrawer}
        onClose={() => onClose()}
        hideBackdrop={false}
        ModalProps={{
          keepMounted: false, // AntDesign::Drawer の destroyOnClose={true} に相当
          disableEscapeKeyDown: true, // esc でも閉じないようにする
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '100%',
              width: '100%',
              maxWidth: 600, // レスポンシブ対応
            },
          },
        }}
      >
        <EditPlace />
      </Drawer>
    </Stack>
  )
}

const useProps = (): PlacesProps => {
  const dispatch = useDispatch()
  const state = useSelector((state: RootState) => state)

  return {
    // -----------------------------------
    // StateProps
    // -----------------------------------

    places: state.placesState.places.places ?? [],
    isLoading: state.placesState.places.isLoading,
    isShowEditPlaceDrawer: state.placesState.places.isShowEditPlaceDrawer,
    segmentIndex: state.placesState.places.segmentIndex,
    activePlaceId: state.placesState.places.activePlaceId,
    count: state.placesState.places.count,
    request: state.placesState.places.request,
    updatedPlaces: state.placesState.places.updatedPlaces,
    filter: state.placesState.places.filter ?? {},

    // -----------------------------------
    // DispatchProps
    // -----------------------------------

    getPlacesV2: (request: GetPlacesV2Request, filter?: GetPlacesFilter) => {
      placesOperations.getPlacesV2(dispatch, request, filter)
    },
    changePlacesV2Page: (
      page: number,
      pageSize?: number,
      filter?: GetPlacesFilter,
    ) => {
      placesOperations.changePlacesV2Page(dispatch, page, pageSize, filter)
    },
    changePlacesV2Size: (pageSize: number, filter?: GetPlacesFilter) => {
      placesOperations.changePlacesV2Size(dispatch, pageSize, filter)
    },
    updateSegmentIndex: (index: number) => {
      placesOperations.updateSegmentIndex(dispatch, index)
    },
    changeIsShowEditPlaceDrawer: (isShow: boolean) => {
      placesOperations.changeIsShowEditPlaceDrawer(dispatch, isShow)
    },
    openAddPlace: () => {
      placesOperations.updateActivePlaceId(dispatch)
      placesOperations.changeIsShowEditPlaceDrawer(dispatch, true)
    },
    openEditPlace: (place: PlaceNode) => {
      placesOperations.updateActivePlaceId(dispatch, place.uuid)
      placesOperations.changeIsShowEditPlaceDrawer(dispatch, true)
    },
    deletePlace: (id: string) => {
      placesOperations.deletePlace(dispatch, id)
    },
    changeUpdatedPlacesFlag: (edited: boolean) => {
      placesOperations.changeUpdatedPlacesFlag(dispatch, edited)
    },
  }
}

export default PlacesScene
